import * as THREE from 'three';
import Scenario from "./scenarioElements/Scenario";
import MaterialAsset from './MaterialAsset';
import QuizManager from './QuizManager';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import PartsCatalogUtils from "./utilities/PartsCatalogUtils";
import Callout from './Callout';
import VisibilityManager from './VisibilityManager';
import Engine from '../classes/Engine';
import { CustomEventTypes, ChecklistType, RenderModes} from '../globals';
import Utils from '../classes/Utilities';
import Input from '../classes/Input';
import {ObjectTriggerTypes, VisibilityModes} from "../interfaces/enums";
import { ModifierKeyCodes, MouseButtons } from '../interfaces/Input';

export default class TriggerManager
{
	constructor()
	{
		this.visibilityToggeledObjects = new Map();
		this.toggeledCallouts = new Map();
		this.interactedVideoObjects = new Map();
		this.raycaster = new THREE.Raycaster();
		this.blocked = false;

		document.addEventListener(CustomEventTypes.simulationStopped, (e) =>
		{
			for (const object of this.visibilityToggeledObjects.keys())
			{
				this.restoreVisibility(object);
			}
			this.visibilityToggeledObjects.clear();

			for (const object of this.toggeledCallouts.keys())
			{
				this.restoreCalloutState(object);
			}
			this.toggeledCallouts.clear();

			for (const materialInstance of this.interactedVideoObjects.values())
			{
				materialInstance.videoTimelineOverride = false;
				const videoElement = materialInstance.videoElementClone;
				if (materialInstance.isVideoPlaying())
				{
					videoElement.pause();
					videoElement.currentTime = 0;
				}
			}
		});

		document.addEventListener(CustomEventTypes.onUnloadScene, (e) =>
		{
			for(let materialInstance of this.interactedVideoObjects.values())
			{
				materialInstance.videoTimelineOverride = false;
				const videoElement = materialInstance.videoElementClone;
				if (materialInstance.isVideoPlaying())
				{
					videoElement.pause();
					videoElement.currentTime = 0;
				}
			}
		});
	}

	restoreCalloutState(object)
	{
		if (this.toggeledCallouts.has(object))
		{
			const storedVisibility = this.toggeledCallouts.get(object);
			Callout.getCallout(object.userData.guid).setVisible(storedVisibility);
			this.toggeledCallouts.delete(object);
		}
	}

	restoreVisibility(object)
	{
		if (this.visibilityToggeledObjects.has(object))
		{
			const storedVisibility = this.visibilityToggeledObjects.get(object);
			object.userData.visibility.overrideParent = storedVisibility.overrideParent;
			object.userData.visibility.customStandaloneMaterial = storedVisibility.customStandaloneMaterial;
			object.userData.visibility.customDeviceMaterial = storedVisibility.customDeviceMaterial;
			VisibilityManager.setObjectsVisibilityMode([object], [storedVisibility.visibilityMode]);
			this.visibilityToggeledObjects.delete(object);
		}
	}

	setVisibilityMode(object, targetVisibility, customMaterial = "")
	{
		if (!object || !object.userData.visibility) return;

		if (!this.visibilityToggeledObjects.has(object))
		{
			const storedVisibility = {
				visibilityMode: object.userData.visibility[VisibilityManager.getRenderMode()],
				overrideParent: object.userData.visibility.overrideParent,
				customStandaloneMaterial: object.userData.visibility.customStandaloneMaterial,
				customDeviceMaterial: object.userData.visibility.customDeviceMaterial,
			};

			this.visibilityToggeledObjects.set(object, storedVisibility);
		}

		object.userData.visibility.overrideParent = true;
		VisibilityManager.setObjectsVisibilityMode([object], [targetVisibility], VisibilityManager.getRenderMode(), customMaterial);
	}

	activateVisibilityToggle(object, targetVisibility, customMaterial = "")
	{
		if (!object || !object.userData.visibility) return;

		if (!this.visibilityToggeledObjects.has(object))
		{
			const storedVisibility = {
				visibilityMode: object.userData.visibility[VisibilityManager.getRenderMode()],
				overrideParent: object.userData.visibility.overrideParent,
				customStandaloneMaterial: object.userData.visibility.customStandaloneMaterial,
				customDeviceMaterial: object.userData.visibility.customDeviceMaterial,
			};

			this.visibilityToggeledObjects.set(object, storedVisibility);
			object.userData.visibility.overrideParent = true;
			VisibilityManager.setObjectsVisibilityMode([object], [targetVisibility], VisibilityManager.getRenderMode(), customMaterial);
		}
		else
		{
			const identicalAppearce = (targetVisibility !== VisibilityModes.Custom)? 
				object.userData.visibility[VisibilityManager.getRenderMode()] === targetVisibility :
				VisibilityManager.getRenderMode() === RenderModes.Standalone ? 
					object.userData.visibility.customStandaloneMaterial === customMaterial : 
					object.userData.visibility.customDeviceMaterial === customMaterial;
			if(identicalAppearce)
			{
				this.restoreVisibility(object);
			}
			else
			{
				VisibilityManager.setObjectsVisibilityMode([object], [targetVisibility], VisibilityManager.getRenderMode(), customMaterial);
			}
		}
	}

	activateCalloutToggle(object)
	{
		if (object && object.userData.annotation && object.userData.annotation.enabled)
		{
			const callout = Callout.getCallout(object.userData.guid);
			if (!this.toggeledCallouts.has(object))
			{
				const initiallyVisible = callout.getVisible();
				callout.setVisible(!initiallyVisible);
				this.toggeledCallouts.set(object, initiallyVisible);
			}
			else
			{
				const storedVisibility = this.toggeledCallouts.get(object);
				callout.setVisible(storedVisibility);
				this.toggeledCallouts.delete(object);
			}
		}
	}

	activateTriggers(object, calloutClicked = false)
	{
		const validTriggers = object.userData.triggers.filter(trigger => !trigger.removedSyncedTrigger && (!calloutClicked || trigger.calloutTrigger));
		for (const trigger of validTriggers)
		{
			this.runTrigger(trigger);
		}

		return validTriggers.length > 0;
	}

	runTrigger(trigger)
	{
		switch (trigger.type)
		{
			case ObjectTriggerTypes.VisibilityToggle:
				{
					const targetObjectGuid = trigger.targetObject;
					if (targetObjectGuid)
					{
						const targetObjectVisibility = trigger.targetObjectVisibility;
						const targetCustomMaterial = trigger.customMaterial;
						const targetObject = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(), targetObjectGuid);
						this.activateVisibilityToggle(targetObject, targetObjectVisibility, targetCustomMaterial);
					}
				}
				break;
			case ObjectTriggerTypes.NextStep:
				const nextStep = Scenario.currentStep().getNextStep();
				if (nextStep)
				{
					Scenario.setCurrentStep(nextStep);
				}
				break;
			case ObjectTriggerTypes.PreviousStep:
				const PreviousStep = Scenario.currentStep().getPreviousStep();
				if (PreviousStep)
				{
					Scenario.setCurrentStep(PreviousStep);
				}
				break;
			case ObjectTriggerTypes.MoveToStep:
				const targetStep = Scenario.getStep(trigger.targetStep);
				if (targetStep)
				{
					Scenario.setCurrentStep(targetStep);
				}
				break;
			case ObjectTriggerTypes.CalloutToggle:
				{
					const targetObjectGuid = trigger.targetObject;
					if (targetObjectGuid)
					{
						const targetObject = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(), targetObjectGuid);
						Engine.getInstance().getTriggerManager().activateCalloutToggle(targetObject);
					}
				}
				break;
			case ObjectTriggerTypes.URL:
				{
					if (trigger.url)
					{
						Utils.openURL(trigger.url);
					}
				}
				break;
			default:
				break;
		}
	}

	blockTriggers(shouldBlock)
	{
		this.blocked = shouldBlock;
	}

	update()
	{
		if (!this.blocked && Input.getMouseButtonUp(MouseButtons.LEFT) &&
		(!Input.getModifier(ModifierKeyCodes.CTRL) && !Input.getModifier(ModifierKeyCodes.COMMAND) &&
		!Input.getModifier(ModifierKeyCodes.ALT) && !Input.getModifier(ModifierKeyCodes.SHIFT)))
		{
			const mousePosition = Input.getMousePosition();
			if (mousePosition.x < -1 || mousePosition.x > 1 || mousePosition.y < -1 || mousePosition.y > 1)
			{
				return;
			}
			this.raycaster.setFromCamera(mousePosition, Engine.getInstance().getCamera());

			const root = Engine.getInstance().getSelectionRoot();
			const intersections = this.raycaster.intersectObjects(root.children, true);
			const pickingManager = Engine.getInstance().getPickingManager();
			const containsQuiz = QuizManager.getQuestion()?.type === ChecklistType.MULTI_OPTION_3D || QuizManager.getQuestion()?.type === ChecklistType.MULTI_OPTION_SELECT_3D;
			const partsCatalogOpen = PartsCatalogUtils.isPartsCatalogOpen();

			for (const intersection of intersections)
			{
				let object = intersection.object;
				let calloutClicked = false;
				if (object instanceof Callout)
				{
					if (object.visible)
					{
						object = object.parent;
						calloutClicked = true;
					}
					else continue;
				}
				else if (!pickingManager.isObjectSelectable(object)) continue;

				if(partsCatalogOpen)
				{					
					const successfulClick = PartsCatalogUtils.onObjectClicked(object);
					if(successfulClick)
					{
						break;
					}
					else
					{
						continue;
					}
				}

				if(containsQuiz)
				{
					if(QuizManager.isQuizObject(object.userData.guid))
					{
						QuizManager.toggle3DAnswer(object.userData.guid);
						break;
					}
					else
					{
						let interacted = false;
						let parent = object.parent;
						while (parent)
						{
							if(QuizManager.isQuizObject(parent.userData.guid))
							{
								QuizManager.toggle3DAnswer(parent.userData.guid);
								interacted = true;
								break;
							}
							parent = parent.parent;
						}
						if(interacted) break;
					}
				}

				//triggers:
				if (object.userData.triggers?.length && this.activateTriggers(object, calloutClicked))
				{
					break;
				}

				//video materials:
				if (object.material && object.userData.guid)
				{
					const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
					if (materialAsset && object.material.map && object.material.map instanceof THREE.VideoTexture)
					{
						let materialInstance = MaterialAsset.getTimelineMaterialInstance(object);
						if(!materialInstance)
						{
							if(object.material.userData.guid)
							{
								materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, object.material);
							}
							else
							{
								materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, materialAsset.internalMaterial);

							}
						}
						if (!this.interactedVideoObjects.has(object.userData.guid))
						{
							this.interactedVideoObjects.set(object.userData.guid, materialInstance);
							materialInstance.videoTimelineOverride = true;
						}

						if (materialInstance.videoElementClone)
						{
							const videoElement = materialInstance.videoElementClone;
							if (materialInstance.isVideoPlaying())
							{
								videoElement.pause();
								break;
							}
							else
							{
								videoElement.play();
								break;
							}
						}
						else
						{
							materialInstance.assignVideoElementClone();
							const videoElement = materialInstance.videoElementClone;
							videoElement.play();
							break;
						}
					}
				}

			}
		}
	}
}
